import { useCallback, useEffect, useRef } from 'react';
import { useStore } from '../../store/store';
import {
  setQsgSection,
  setQsgSectionError,
  setQsgSectionLoading,
} from '../../store/slices/qsg/actions';
import { getSectionByPath } from '../../fetch-data/graphql/helpers';
import { findSectionRecursive } from '../../utils/navigation';
import {
  normalizeSectionId,
  getSectionFromPath,
  isGuideRoute,
} from '../../utils/query-param-helpers';
import { useHistory } from '../use-history';

export const useSectionLoader = (client: any, hasInit: boolean) => {
  const loadingSectionsRef = useRef<Set<string>>(new Set());
  const [state, dispatch] = useStore();
  const history = useHistory();

  const currentSectionId = isGuideRoute(history.location.pathname)
    ? getSectionFromPath(history.location.pathname)
    : '';

  const { qsgNavigation, qsgSectionMap, qsgSectionErrors } = state.qsg;

  const sections = qsgNavigation?.sections ?? [];
  const sectionMap = qsgSectionMap;
  const sectionErrors = qsgSectionErrors;

  const loadSection = useCallback(
    async (sectionPath: string, sectionId: string) => {
      if (loadingSectionsRef.current.has(sectionPath)) return;

      loadingSectionsRef.current.add(sectionPath);

      // Normalize section ID to match how it's stored in the map
      const normalizedSectionId = normalizeSectionId(sectionId);
      dispatch(setQsgSectionLoading(normalizedSectionId, true));
      dispatch(setQsgSectionError(normalizedSectionId, null));

      try {
        const section = await getSectionByPath(sectionPath, client);

        if (section) dispatch(setQsgSection(section));
        else
          dispatch(
            setQsgSectionError(normalizedSectionId, 'Section not found')
          );
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to load section';
        dispatch(setQsgSectionError(normalizedSectionId, errorMessage));
      } finally {
        loadingSectionsRef.current.delete(sectionPath);
      }
    },
    [client, dispatch]
  );

  useEffect(() => {
    if (!hasInit || !currentSectionId) return;

    const sectionToLoad = findSectionRecursive(sections, currentSectionId);

    if (sectionToLoad) {
      // Normalize section ID for consistent map lookups
      const normalizedSectionId = normalizeSectionId(sectionToLoad.sectionId);
      const shouldLoadSection =
        !sectionMap[normalizedSectionId] &&
        !loadingSectionsRef.current.has(sectionToLoad._path) &&
        !sectionErrors[normalizedSectionId];

      if (shouldLoadSection)
        loadSection(sectionToLoad._path, sectionToLoad.sectionId);
    }
  }, [
    hasInit,
    currentSectionId,
    sections,
    sectionMap,
    sectionErrors,
    loadSection,
  ]);

  return {
    loadSection,
  };
};
